import React, {useState} from 'react'
import {Link} from 'react-router-dom'
import {BsCalendar2Date} from 'react-icons/bs'
import {FaUserGraduate, FaUserTie} from 'react-icons/fa6'
import {FaChalkboardTeacher} from 'react-icons/fa'
import {PiBookOpenText, PiCrownSimpleFill} from 'react-icons/pi'
import useTranslate from 'modules/local/useTranslate'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import {renderFromToDate} from 'helpers/datetimeHelpers'

// Lịch sử hoạt động của 1 người (vai trò Học viên + Giảng viên, 3 vai trò con của Giảng viên:
// Ban điều hành/Người phụ trách lớp/Giảng viên theo lịch) - dùng chung cho mọi nơi hiển thị dữ
// liệu từ MemberActivityService (course_getMemberActivityHistory_Api hoặc field "activity_history"
// nhúng sẵn trong StudentTransformer/TeacherTransformer) - KHÔNG copy dán JSX ra nhiều nơi, sửa 1
// chỗ áp dụng hết. Dữ liệu đã được BE sắp sẵn thứ tự (đang/sắp diễn ra trước, đã kết thúc sau).
export const MemberActivityHistory = ({
                                          is_student = false,
                                          student_courses = [],
                                          is_teacher = false,
                                          teacher_courses = [],
                                      }) => {
    const t = useTranslate()

    const [courseTeacherLimit, setTeacherLimit] = useState(2)
    const [courseStudentLimit, setStudentLimit] = useState(2)

    const visibleTeacher = teacher_courses.slice(0, courseTeacherLimit)
    const visibleStudent = student_courses.slice(0, courseStudentLimit)

    if (!is_student && !is_teacher) return null

    return (
        <div className="flex flex-col gap-4">
            {is_student && student_courses.length > 0 && (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5">
                        <FaUserGraduate size={12} className="text-indigo-400"/>
                        <span className="font-bold text-2xs text-slate-400 uppercase tracking-wider">
                            {t('student')}
                        </span>
                    </div>
                    {visibleStudent.map((e, i) => {
                        const {course, registered_classes = []} = e ?? {}
                        return (
                            <div key={i} className="p-3 flex items-start gap-2.5 border border-color-50 rounded-xl">
                                <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                                    <PiBookOpenText size={16} className="text-indigo-500"/>
                                </div>
                                <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                                    <Link to={getLinkToDetail(course)} className="no-underline font-bold text-sm text-color-000 break-words leading-snug">
                                        {getTitle(course)}
                                    </Link>
                                    {course?.time_open && (
                                        <div className="flex items-center gap-1 font-medium text-2xs text-color-200">
                                            <BsCalendar2Date size={11} className="flex-shrink-0"/>
                                            <span>{renderFromToDate(course?.time_open, course?.time_close)}</span>
                                        </div>
                                    )}
                                    {registered_classes.length > 0 && (
                                        <div className="flex flex-col gap-1.5">
                                            {registered_classes.map((c, ci) => (
                                                <div key={ci} className="flex flex-col gap-0.5 pl-2.5 border-l-2 border-indigo-100">
                                                    <div className="text-xs text-color-300 break-words">
                                                        {t('learn in the class')}{': '}
                                                        <Link to={getLinkToDetail(c)} className="font-semibold text-indigo-500 no-underline">
                                                            {getTitle(c)}
                                                        </Link>
                                                    </div>
                                                    {c?.from_date && (
                                                        <div className="flex items-center gap-1 text-2xs text-color-300">
                                                            <BsCalendar2Date size={11} className="text-color-300 flex-shrink-0"/>
                                                            <span>{renderFromToDate(c?.from_date, c?.to_date)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                    {student_courses.length > courseStudentLimit && (
                        <div className="mt-2 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setStudentLimit(prev => prev + 2)}
                                className="text-color-400 hover:text-secondary-500 tracking-wider">
                                {t('see more')}
                            </button>
                        </div>
                    )}
                </div>
            )}
            {is_teacher && teacher_courses.length > 0 && (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5">
                        <FaUserTie size={12} className="text-amber-500"/>
                        <span className="font-bold text-2xs text-slate-400 uppercase tracking-wider">
                            {t('teacher')}
                        </span>
                    </div>
                    {visibleTeacher.map((entry, i) => {
                        const {course, committee_position, coordinated_classes = [], teaching_schedules = []} = entry ?? {}
                        return (
                            <div key={i} className="p-3 flex items-start gap-2.5 border border-color-50 rounded-xl">
                                <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                                    <PiBookOpenText size={16} className="text-amber-500"/>
                                </div>
                                <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                                    <Link to={getLinkToDetail(course)} className="no-underline font-bold text-sm text-color-000 break-words leading-snug">
                                        {getTitle(course)}
                                    </Link>
                                    {course?.time_open && (
                                        <div className="flex items-center gap-1 font-medium text-2xs text-color-200">
                                            <BsCalendar2Date size={11} className="flex-shrink-0"/>
                                            <span>{renderFromToDate(course?.time_open, course?.time_close)}</span>
                                        </div>
                                    )}
                                    <div className="flex flex-col gap-1.5 pl-2.5 border-l-2 border-amber-100">
                                        {/* 1. Ban điều hành khóa học - chức vụ cụ thể */}
                                        {committee_position && (
                                            <div className="flex items-start gap-1.5">
                                                <PiCrownSimpleFill size={12} className="text-amber-500 flex-shrink-0 mt-0.5"/>
                                                <div className="flex flex-col">
                                                    <span className="text-xs text-color-300">
                                                        {t('course committee')}{': '}
                                                        <span className="font-semibold text-color-100">
                                                            {committee_position?.position}
                                                        </span>
                                                    </span>
                                                    {committee_position?.group?.title && (
                                                        <span className="text-2xs text-color-300 italic">
                                                            {committee_position.group.title}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {/* 2. Người phụ trách lớp - lớp phụ trách */}
                                        {coordinated_classes.length > 0 && (
                                            <div className="flex flex-col gap-1">
                                                {coordinated_classes.map((cc, ci) => (
                                                    <div key={ci} className="flex items-start gap-1.5">
                                                        <FaUserTie size={11} className="text-indigo-500 flex-shrink-0 mt-0.5"/>
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-color-300 break-words">
                                                                {t('class coordinator')}{': '}
                                                                <Link to={getLinkToDetail(cc)} className="font-semibold text-indigo-500 no-underline">
                                                                    {getTitle(cc)}
                                                                </Link>
                                                            </span>
                                                            {cc?.from_date && (
                                                                <div className="flex items-center gap-1 text-2xs text-color-300">
                                                                    <BsCalendar2Date size={11} className="text-color-300 flex-shrink-0"/>
                                                                    <span>{renderFromToDate(cc?.from_date, cc?.to_date)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {/* 3. Giảng viên - lớp/môn/lịch dạy */}
                                        {teaching_schedules.length > 0 && (
                                            <div className="flex flex-col gap-1">
                                                {teaching_schedules.map((ts, tsi) => (
                                                    <div key={tsi} className="flex items-start gap-1.5">
                                                        <FaChalkboardTeacher size={11} className="text-amber-500 flex-shrink-0 mt-0.5"/>
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-color-300 break-words">
                                                                {t('teaching the class')}{': '}
                                                                <Link to={getLinkToDetail(ts?.course_class)} className="font-semibold text-amber-600 no-underline">
                                                                    {getTitle(ts?.course_class)}
                                                                </Link>
                                                                {ts?.subject?.title && ` - ${ts.subject.title}`}
                                                            </span>
                                                            {ts?.from_date && (
                                                                <div className="flex items-center gap-1 text-2xs text-color-300">
                                                                    <BsCalendar2Date size={11} className="text-color-300 flex-shrink-0"/>
                                                                    <span>{renderFromToDate(ts?.from_date, ts?.to_date)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                    {teacher_courses.length > courseTeacherLimit && (
                        <div className="mt-2 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setTeacherLimit(prev => prev + 2)}
                                className="text-color-400 hover:text-secondary-500 tracking-wider">
                                {t('see more')}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default MemberActivityHistory
